import { useEffect, useState } from "react";
import { adminApi, formatApiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { ImageUploadField } from "@/components/admin/ImageUploadField";
import { SafeImage } from "@/components/SafeImage";

const EMPTY = {
  title: "",
  description: "",
  price: "",
  day: "",
  image_url: "",
  active: true,
};

export default function SpecialsManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/specials");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  function startNew() {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  }
  function startEdit(s) {
    setEditing(s);
    setForm({
      title: s.title || "",
      description: s.description || "",
      price: s.price || "",
      day: s.day || "",
      image_url: s.image_url || "",
      active: !!s.active,
    });
    setOpen(true);
  }
  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await adminApi.patch(`/admin/specials/${editing.id}`, form);
        toast.success("Special updated");
      } else {
        await adminApi.post("/admin/specials", form);
        toast.success("Special added");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  }
  async function toggleActive(s) {
    try {
      await adminApi.patch(`/admin/specials/${s.id}`, { active: !s.active });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }
  async function remove(s) {
    if (!window.confirm(`Delete "${s.title}"?`)) return;
    try {
      await adminApi.delete(`/admin/specials/${s.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  return (
    <div data-testid="specials-manager" className="space-y-6">
      <div className="flex justify-between items-center">
        <p className="text-sm text-[var(--trio-muted)] max-w-xl">
          Daily and weekly plates. Toggle <strong>Live</strong> off to hide
          one without deleting it.
        </p>
        <button
          onClick={startNew}
          data-testid="special-new-btn"
          className="inline-flex items-center gap-2 bg-[var(--trio-primary)] hover:bg-[var(--trio-primary-hover)] text-white px-5 py-2.5 text-xs tracking-widest uppercase font-bold transition-colors"
        >
          <Plus className="w-4 h-4" /> New Special
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-[var(--trio-muted)]">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {items.map((s) => (
            <article
              key={s.id}
              data-testid={`special-row-${s.id}`}
              className="bg-[var(--trio-surface)] border border-[var(--trio-border)] p-5 flex gap-4"
            >
              {s.image_url && (
                <SafeImage src={s.image_url} alt={s.title} className="w-24 h-24 object-cover shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] tracking-[0.25em] uppercase text-[var(--trio-primary)] font-bold">
                    {s.day}
                  </span>
                  {s.price && (
                    <span className="font-display text-lg">{s.price}</span>
                  )}
                </div>
                <h3 className="font-display uppercase text-xl tracking-tight">
                  {s.title}
                </h3>
                <p className="text-sm text-[var(--trio-muted)] line-clamp-2 mt-1">
                  {s.description}
                </p>
                <div className="mt-3 flex items-center gap-3 text-xs">
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <Switch
                      checked={s.active}
                      onCheckedChange={() => toggleActive(s)}
                      data-testid={`special-toggle-${s.id}`}
                    />
                    <span className="uppercase tracking-widest">Live</span>
                  </label>
                  <button
                    onClick={() => startEdit(s)}
                    data-testid={`special-edit-${s.id}`}
                    className="ml-auto inline-flex items-center gap-1.5 hover:text-[var(--trio-primary)]"
                  >
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </button>
                  <button
                    onClick={() => remove(s)}
                    data-testid={`special-delete-${s.id}`}
                    className="inline-flex items-center gap-1.5 hover:text-[var(--trio-primary)]"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent aria-describedby={undefined}
          data-testid="special-form-dialog"
          className="bg-[var(--trio-bg)] border-[var(--trio-border)] rounded-none max-w-lg"
        >
          <DialogHeader>
            <DialogTitle className="font-display uppercase text-2xl tracking-tight">
              {editing ? "Edit Special" : "New Special"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <FieldRow label="Title" value={form.title} onChange={(v) => setForm((p) => ({ ...p, title: v }))} required testid="special-field-title" />
            <div>
              <Label className="text-xs tracking-widest uppercase">Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                rows={3}
                required
                data-testid="special-field-description"
                className="mt-2 rounded-none border-[var(--trio-border)] bg-[var(--trio-bg)]"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FieldRow label="Day" value={form.day} onChange={(v) => setForm((p) => ({ ...p, day: v }))} placeholder="Friday" required testid="special-field-day" />
              <FieldRow label="Price" value={form.price} onChange={(v) => setForm((p) => ({ ...p, price: v }))} placeholder="$19" testid="special-field-price" />
            </div>
            <ImageUploadField value={form.image_url} onChange={(v) => setForm((p) => ({ ...p, image_url: v }))} testid="special-field-image" />
            <label className="inline-flex items-center gap-2">
              <Switch
                checked={form.active}
                onCheckedChange={(v) => setForm((p) => ({ ...p, active: v }))}
                data-testid="special-field-active"
              />
              <span className="text-xs uppercase tracking-widest">Live</span>
            </label>
            <button
              type="submit"
              disabled={saving}
              data-testid="special-save"
              className="w-full bg-[var(--trio-primary)] hover:bg-[var(--trio-primary-hover)] text-white py-3 text-xs tracking-widest uppercase font-bold disabled:opacity-60 transition-colors"
            >
              {saving ? "Saving…" : editing ? "Update" : "Add Special"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FieldRow({ label, value, onChange, required, placeholder, testid }) {
  return (
    <div>
      <Label className="text-xs tracking-widest uppercase">{label}</Label>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        data-testid={testid}
        className="mt-2 rounded-none border-[var(--trio-border)] bg-[var(--trio-bg)]"
      />
    </div>
  );
}
